import axiosInstance from "../axios/axios-instance";
import { pathAPI } from "../utils/pathApi";
import {
  ApiResponse,
  PackageResponse,
  PackageDetailResponse,
  PackageCreateRequest,
  PackageUpdateRequest,
  PackageMenuAssignment,
  PackageMenuResponse,
  PackageCountryPrice,
  PackageCountryPriceCreateRequest,
  UnassignedMenu,
} from "../@type/package.types";

export const PackageService = {
  /**
   * Get all packages with pagination
   */
  getAllPackages: (params?: { page?: number; size?: number }) => {
    return axiosInstance.get<{ content: PackageResponse[]; totalElements: number, data: any }>(
      pathAPI.packages,
      { params }
    );
  },

  /**
   * Get package by ID with assigned menus
   */
  getPackageById: (id: number) => {
    return axiosInstance.get<ApiResponse<PackageDetailResponse>>(`${pathAPI.packages}/${id}`);
  },

  /**
   * Create new package
   */
  createPackage: (data: PackageCreateRequest) => {
    return axiosInstance.post<ApiResponse<PackageResponse>>(pathAPI.packages, data);
  },

  /**
   * Update existing package
   */
  updatePackage: (id: number, data: PackageUpdateRequest) => {
    return axiosInstance.put<ApiResponse<PackageResponse>>(`${pathAPI.packages}/${id}`, data);
  },

  /**
   * Delete package
   */
  deletePackage: (id: number) => {
    return axiosInstance.delete<ApiResponse<void>>(`${pathAPI.packages}/${id}`);
  },

  /**
   * Assign menus to package
   */
  assignMenusToPackage: (packageId: number, menuItemIds: number[]) => {
    const url = pathAPI.packageMenus.replace(':packageId', packageId.toString());
    return axiosInstance.put<ApiResponse<void>>(url, { menu_item_ids: menuItemIds });
  },

  /**
   * Get assigned menus for package
   */
  getPackageMenus: (packageId: number) => {
    const url = pathAPI.packageMenus.replace(':packageId', packageId.toString());
    return axiosInstance.get<ApiResponse<PackageMenuResponse>>(url);
  },

  /**
   * Get unassigned menus (menus not assigned to any package)
   */
  getUnassignedMenus: () => {
    return axiosInstance.get<ApiResponse<UnassignedMenu[]>>(pathAPI.unassignedMenus);
  },

  /**
   * Get country prices for package
   */
  getPackageCountryPrices: (packageId: number) => {
    const url = pathAPI.packageCountryPrices.replace(':packageId', packageId.toString());
    return axiosInstance.get<ApiResponse<PackageCountryPrice[]>>(url);
  },

  /**
   * Add/Update country price for package
   */
  upsertCountryPrice: (packageId: number, data: PackageCountryPriceCreateRequest) => {
    const url = pathAPI.packageCountryPrices.replace(':packageId', packageId.toString());
    return axiosInstance.post<ApiResponse<PackageCountryPrice>>(url, data);
  },

  /**
   * Delete country price
   */
  deleteCountryPrice: (packageId: number, countryId: number) => {
    const url = `${pathAPI.packageCountryPrices.replace(':packageId', packageId.toString())}/${countryId}`;
    return axiosInstance.delete<ApiResponse<void>>(url);
  },

  /**
   * Change pricing strategy (global <-> per-country)
   */
  updatePricingStrategy: (packageId: number, strategy: 'global' | 'per_country', copyPrices?: boolean) => {
    const url = pathAPI.packagePricingStrategy.replace(':packageId', packageId.toString());
    return axiosInstance.put<ApiResponse<void>>(url, {
      pricing_strategy: strategy,
      copy_global_prices: copyPrices
    });
  },

  /**
   * Copy global price to all countries
   */
  copyGlobalToAllCountries: (packageId: number) => {
    const url = `${pathAPI.packages}/${packageId}/prices/copy-global`;
    return axiosInstance.post<ApiResponse<void>>(url);
  },
};
